import React, { useEffect } from "react";
import { TextField, Typography } from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { useHeader } from "../../../contexts";
import { string, object } from "yup";
import {
  useConsumerAuth,
  useYupValidation,
  usePreferences
} from "../../../hooks";
import { validDateOfBirth, formatDate } from "../../../utils";
import moment from "moment";
import { useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";

const focusOnErrors = createDecorator();

const validationSchema = object({
  first_name: string()
    .required()
    .trim()
    .label("First name"),
  middle_name: string()
    .trim()
    .label("Middle name"),
  last_name: string()
    .required()
    .trim()
    .label("Last name"),
  dob: string()
    .test("Valid date of birth", "Date of birth is invalid", value =>
      value ? validDateOfBirth(value) : false
    )
    .label("Date of birth")
});

const SchedulePatientDetails = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const { setTitle } = useHeader();
  const { first_name, last_name, dob } = useConsumerAuth();
  const { dateFormat } = usePreferences();

  useEffect(() => {
    setTitle(t("schedule.patient_details.title"));
  }, []);

  const { validate } = useYupValidation();

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    // Calculate age
    const age = moment().diff(data.dob, "years");

    updateScheduleData({ patientDetails: data, age });
  };

  /**
   * Set initial form values
   */
  const setInitialValues = () => {
    let initialValues = {};

    if (scheduleData.isPatient && scheduleData.isPatient === true) {
      initialValues = {
        ...initialValues,
        first_name,
        last_name,
        dob
      };
    }

    return { ...initialValues, ...scheduleData.patientDetails };
  };

  return (
    <Form
      onSubmit={validateForm}
      initialValues={setInitialValues()}
      decorators={[focusOnErrors]}
      render={({ handleSubmit, submitErrors: errors = {} }) => (
        <form data-cy="schedule-screen-patient-details" onSubmit={handleSubmit}>
          <IntroText>{t("schedule.patient_details.intro_text")}</IntroText>
          <Typography paragraph align="center">
            {t("schedule.patient_details.secondary_text")}
          </Typography>
          <Field
            name="first_name"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.patient_details.form.first_name.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.first_name}
                helperText={errors.first_name}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-first-name"
                }}
              />
            )}
          />
          <Field
            name="middle_name"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.patient_details.form.middle_name.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.middle_name}
                helperText={errors.middle_name}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-middle-name"
                }}
              />
            )}
          />
          <Field
            name="last_name"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.patient_details.form.last_name.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.last_name}
                helperText={errors.last_name}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-last-name"
                }}
              />
            )}
          />
          <Field
            name="dob"
            parse={value => formatDate(value, dateFormat.default, "YYYY-MM-DD")}
            format={value =>
              formatDate(value, "YYYY-MM-DD", dateFormat.default)
            }
            render={({ input }) => (
              <NumberFormat
                {...input}
                format="##/##/####"
                placeholder={dateFormat.default}
                label={t("schedule.patient_details.form.dob.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.dob}
                helperText={errors.dob}
                customInput={TextField}
                inputProps={{
                  "data-cy": "input-dob"
                }}
              />
            )}
          />
          <FormActionDrawer
            setDrawerClearance={setDrawerClearance}
            loading={isSubmitting}
          />
        </form>
      )}
    />
  );
};

export { SchedulePatientDetails };
